import { useCallback, useSyncExternalStore } from 'react';
import { getTheme, onTheme, otherTheme, setTheme, type Theme } from '../lib/theme';

/**
 * Which way the page is lit, as React sees it.
 *
 * Unlike the language, this needs no provider. The decision lives in
 * lib/theme.ts — a module, for the reasons that file gives — and almost
 * nothing in the tree has to *read* it: the theme is applied as an attribute
 * on <html> and the whole product follows it through CSS custom properties
 * without a single component knowing. The two readers that remain are the
 * control itself, which has to say what it will switch to, and the commit
 * graph, whose lane ramp is SVG fills and therefore cannot be a token.
 *
 * `useSyncExternalStore` rather than a context and an effect, because that is
 * exactly what this is: a value owned outside React, with a subscribe and a
 * read.
 *
 * The server snapshot is the same read as the client one, and deliberately not
 * a hardcoded `DEFAULT_THEME`. `getTheme()` is already safe in a process with
 * no document — lib/theme.ts detects nothing and answers with the default — so
 * pinning the constant here would not make it safer, it would only make the
 * module's answer unreachable from a render that had set it. That is not
 * hypothetical: the product is client-rendered, but build/shot renders these
 * components to static markup to look at them, and a snapshot that ignored
 * `setTheme` drew the light theme with the dark theme's commit graph.
 */
export interface ThemeValue {
  theme: Theme;
  /** The theme a click would move to. A two-theme product's control is a switch. */
  next: Theme;
  setTheme(theme: Theme): void;
  toggle(): void;
}

export function useTheme(): ThemeValue {
  const theme = useSyncExternalStore(onTheme, getTheme, getTheme);
  const toggle = useCallback(() => setTheme(otherTheme()), []);
  return { theme, next: otherTheme(theme), setTheme, toggle };
}
